import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Form, Nav, Row } from 'react-bootstrap-5';
import { verifyLogo, deleteLogo, revertLogo, replaceLogo, fetchReplaceModalData } from './api';

const CARD_STATES = {
  UNVERIFIED: 'unverified',
  PENDING: 'pending',
  VERIFIED: 'verified',
  DELETED: 'deleted'
};

export default function LogoVerifyCard({ companyId, logo, logoDefaults, onShowReplaceModal }) {
  const [cardState, setCardState] = useState(getInitialState(logo));
  const [logoName, setLogoName] = useState(logo.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [disabled, setDisabled] = useState(logo.verified || logo.deleted);

  function getInitialState(logo) {
    if (logo.deleted) return CARD_STATES.DELETED;
    if (logo.verified) return CARD_STATES.VERIFIED;
    return CARD_STATES.UNVERIFIED;
  }

  const loadDidYouMean = useCallback((text) => {
    if (!logoDefaults || !text) return;

    const trimmedText = text.trim();
    if (trimmedText.length <= 2) {
      setSuggestions([]);
      return;
    }

    const results = logoDefaults.filter(name => (
      name.toLowerCase().includes(trimmedText.toLowerCase())
    ));

    if (results.length > 0 && results[0] !== trimmedText) {
      setSuggestions(results.slice(0, 5)); // Limit to 5 suggestions
    } else {
      setSuggestions([]);
    }
  }, [logoDefaults]);

  useEffect(() => {
    loadDidYouMean(logoName);
  }, [logoName, loadDidYouMean]);

  const handleNameChange = (e) => {
    setLogoName(e.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    setLogoName(suggestion);
    setSuggestions([]);
  };

  const extractOldId = (modalUrl) => {
    const pathMatch = modalUrl.match(/replace\/(\d+)/);
    if (pathMatch) return pathMatch[1];

    const queryMatch = modalUrl.match(/[?&]old_id=(\d+)/);
    if (queryMatch) return queryMatch[1];

    return null;
  };

  const handleVerify = async () => {
    setCardState(CARD_STATES.PENDING);

    try {
      await verifyLogo(logo.id, logoName, companyId);
      setCardState(CARD_STATES.VERIFIED);
      setDisabled(true);
    } catch (error) {
      // Check if it's a conflict (409) - need to show replace modal
      if (error.response?.status === 409 || error.status === 409) {
        const data = error.response?.data || error;
        if (data.modal) {
          // Extract old_id from the modal URL
          const modalUrl = data.modal;
          const oldId = extractOldId(modalUrl);
          if (oldId) {
            try {
              const modalResponse = await fetchReplaceModalData(logo.id, oldId, companyId);
              const oldLogoUrl = modalResponse?.oldLogoUrl || '';
              const newLogoUrl = modalResponse?.newLogoUrl || logo.logoUrl;

              onShowReplaceModal({
                oldId,
                oldLogoUrl,
                newLogoUrl,
                onConfirm: async () => {
                  try {
                    await replaceLogo(logo.id, oldId, companyId);
                    window.location.reload();
                  } catch (error) {
                    setCardState(CARD_STATES.UNVERIFIED);
                  }
                },
                onCancel: () => {
                  setCardState(CARD_STATES.UNVERIFIED);
                }
              });
            } catch (modalError) {
              // Failed to load replace modal, silently continue
            }
          }
          setCardState(CARD_STATES.UNVERIFIED);
        } else {
          setCardState(CARD_STATES.UNVERIFIED);
        }
      } else {
        setCardState(CARD_STATES.UNVERIFIED);
      }
    }
  };

  const handleDelete = async () => {
    setDisabled(true);
    setCardState(CARD_STATES.PENDING);

    try {
      await deleteLogo(logo.id, companyId);
      setCardState(CARD_STATES.DELETED);
    } catch (error) {
      setCardState(CARD_STATES.UNVERIFIED);
      setDisabled(false);
    }
  };

  const handleRevert = async () => {
    const originalState = cardState;
    setCardState(CARD_STATES.PENDING);

    try {
      await revertLogo(logo.id, companyId);
      setCardState(CARD_STATES.UNVERIFIED);
      setDisabled(false);
    } catch (error) {
      setCardState(originalState);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <Form className="form-vertical-field-container h-100 w-100" onSubmit={handleSubmit}>
      <input name="company_id" type="hidden" value={companyId} />

      <Card className={`h-100 w-100 ${cardState}`}>
        <Card.Body>
          <Row className="py-4">
            <Col className="mx-auto" lg={4} md={6} xs={12}>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ height: '125px' }}
              >
                <img
                  alt={logoName}
                  className="d-block object-fit-contain"
                  src={logo.logoUrl}
                  style={{ height: '125px' }}
                />
              </div>
            </Col>
          </Row>
          <div>
            <Form.Group className="mb-3" controlId={`master_logo_name_${logo.id}`}>
              <Form.Label>Name</Form.Label>
              <Form.Control
                disabled={disabled}
                type="text"
                value={logoName}
                onChange={handleNameChange}
              />
            </Form.Group>
            {suggestions.length > 0 && (
              <p className="form-text mb-0">
                <strong>Did you mean:</strong>{' '}
                {suggestions.map((suggestion, index) => (
                  <React.Fragment key={suggestion}>
                    {index > 0 && ', '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSuggestionClick(suggestion);
                      }}
                    >
                      {suggestion}
                    </a>
                  </React.Fragment>
                ))}
              </p>
            )}
          </div>
        </Card.Body>

        <Card.Footer className={getFooterClassName(cardState)}>
          {cardState === CARD_STATES.UNVERIFIED && (
            <div>
              <Nav fill variant="pills">
                <Nav.Item>
                  <Nav.Link
                    className="d-flex align-items-center justify-content-center gap-2"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDelete();
                    }}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    className="d-flex align-items-center justify-content-center gap-2"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleVerify();
                    }}
                  >
                    <i className="fas fa-check-circle"></i> Verify
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          )}

          {cardState === CARD_STATES.VERIFIED && (
            <div>
              <div className="float-end">
                <a
                  className="text-decoration-none"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRevert();
                  }}
                >
                  <i className="fas fa-undo"></i> <small>Revert</small>
                </a>
              </div>
              <strong className="fw-semibold">VERIFIED</strong>
            </div>
          )}

          {cardState === CARD_STATES.DELETED && (
            <div>
              <div className="float-end">
                <a
                  className="text-decoration-none"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRevert();
                  }}
                >
                  <i className="fas fa-undo"></i> <small>Revert</small>
                </a>
              </div>
              <strong className="fw-semibold">DELETED</strong>
            </div>
          )}

          {cardState === CARD_STATES.PENDING && (
            <div className="text-center">
              <div className="spinner-border" /> Working...
            </div>
          )}
        </Card.Footer>
      </Card>
    </Form>
  );
}

function getFooterClassName(state) {
  switch (state) {
  case CARD_STATES.UNVERIFIED:
    return 'p-0';
  case CARD_STATES.VERIFIED:
    return 'bg-mountain-meadow-100 text-mountain-meadow-700';
  case CARD_STATES.DELETED:
    return 'bg-solid-pink-100 text-solid-pink-700';
  default:
    return '';
  }
}

LogoVerifyCard.propTypes = {
  companyId: PropTypes.number.isRequired,
  logo: PropTypes.shape({
    deleted: PropTypes.bool,
    id: PropTypes.number.isRequired,
    logoUrl: PropTypes.string.isRequired,
    name: PropTypes.string,
    verified: PropTypes.bool
  }).isRequired,
  logoDefaults: PropTypes.arrayOf(PropTypes.string),
  onShowReplaceModal: PropTypes.func.isRequired
};

LogoVerifyCard.defaultProps = {
  logoDefaults: []
};
